<template>
  <b-modal
    :id="id"
    size="xl"
    @shown="onShown"
    @hidden="onHidden"
  >
    <template #modal-title>
      {{ trans(!!booking && !!booking.id ? 'booking' : 'create_booking') }}
      <small v-if="!!booking && !!booking.id" class="text-muted ml-2">
        #{{ booking.id }}
      </small>
      <booking-status-badge
        v-if="!!booking && !!booking.id"
        v-model="booking.status"
        :admin-name="!!booking && !!booking.admin ? booking.admin.name : null"
      ></booking-status-badge>
    </template>

    <p v-if="!canModify" class="text-danger">
      <i class="fa fa-fw fa-info-circle"></i>
      <em>{{ trans('change_no_permission') }}</em>
    </p>

    <b-tabs v-model="tab" content-class="mt-3">
      <b-tab :title="trans('reservations')" lazy>
        <b-form-group :label="trans('name')">
          <b-form-input v-model="name" :state="nameState" :disabled="busyOrCannotModify" />
        </b-form-group>

        <b-row>
          <b-col>
            <label>
              {{ trans('location_reservations') }}
            </label>
          </b-col>
          <b-col class="text-right">
            <b-button variant="link" class="p-0" @click="show_apply_package_rules = !show_apply_package_rules">
              {{ trans('apply_package_rules') }}
              <i :class="`fa fa-fw fa-caret-${show_apply_package_rules ? 'up' : 'down'}`"></i>
            </b-button>
          </b-col>
        </b-row>

        <div v-if="organization.has_bookings" class="my-3">
          <b-card v-if="show_apply_package_rules">
            <b-row>
              <b-col class="col-5 pr-0">
                <b-form-group :label="trans('package')">
                  <b-form-select v-model="package_id" :options="packageOptions" :disabled="busyOrCannotModify" />
                </b-form-group>
              </b-col>
              <b-col class="col-3 pr-0">
                <b-form-group :label="trans('date')">
                  <datetime v-model="start_date" :config="dateOptions" :disabled="busyOrCannotModify" />
                </b-form-group>
              </b-col>
              <b-col class="col-2 pr-0">
                <b-form-group :label="trans('time')">
                  <datetime v-model="start_time" :config="timeOptions" :disabled="busyOrCannotModify" />
                </b-form-group>
              </b-col>
              <b-col class="col-2">
                <b-form-group>
                  <label class="d-block">&nbsp;</label>
                  <b-button variant="primary" @click="onApplyPackage">
                    {{ trans('apply') }}
                  </b-button>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
        </div>

        <b-row v-if="passedCount > 0" class="mb-2">
          <b-col>
            <b-form-checkbox v-model="show_passed_events" :disabled="busyOrCannotModify">
              {{ trans('show_passed_events') }} ({{ passedCount }})
            </b-form-checkbox>
          </b-col>
        </b-row>
        <booking-events-cache
          :organization="organization"
          @fetch-events="fetch_events_fn = $event"
        ></booking-events-cache>
        <staff-availability-events-cache
          :organization="organization"
          @fetch-availabilities="fetch_availabilities_fn = $event"
        ></staff-availability-events-cache>
        <div v-if="visible && !!fetch_events_fn && !!fetch_availabilities_fn">
          <book-location          
            v-for="(event, index) in events"
            :key="index"
            v-if="canShow(event, index)"
            class="mb-2"
            v-model="events[index]"
            :variant="getVariant(event)"
            :organization="organization"
            :booking="booking"
            :locations="locations"
            :index="index"
            :is-first="isFirstShown(event, index)"
            :disabled="busyOrCannotModify"
            :mode="mode"
            :fetch-events="fetch_events_fn"
            :fetch-staff-availability-events="fetch_availabilities_fn"
            @delete="onDeleteRow($event)"
            @location="onLocationChanged"
          ></book-location>
        </div>
        <b-row class="mt-3">
          <b-col class="col-7">
            <b-form-group>
              <b-button variant="primary" size="sm" :disabled="busyOrCannotModify" @click="onAddRow">
                <i class="fa fa-fw fa-plus"></i>
                {{ trans('resource') }}
              </b-button>
              <b-button variant="outline-primary" size="sm" class="ml-2" :disabled="busyOrCannotModify" @click="onAddRow(true)">
                <i class="fa fa-fw fa-plus"></i>
                {{ trans('sequence') }}
              </b-button>
              <b-button variant="outline-primary" size="sm" class="ml-2" :disabled="busyOrCannotModify" @click="onClickAddRecurrence">
                <i class="fa fa-fw fa-plus"></i>
                {{ trans('recurrence') }}
              </b-button>
              <b-button v-if="events.length > 1" variant="outline-secondary" size="sm" class="ml-2" @click="onClickSort">
                <i class="fa fa-fw fa-sort-amount-down-alt"></i>
                {{ trans('sort') }}
              </b-button>
            </b-form-group>
          </b-col>
          <b-col class="col-4 pr-0 text-right">
            <b-form-group>
              <b-form-radio-group
                v-model="mode"
                button-variant="outline-secondary"
                size="sm"
                :options="modeOptions"
                :disabled="busyOrCannotModify"
                buttons
              ></b-form-radio-group>
            </b-form-group>
          </b-col>
          <b-col class="col-1 pl-0">
            &nbsp;
          </b-col>
        </b-row>
        <b-row v-if="hasEquipmentWithSkus" class="mt-2">
          <b-col v-if="!scan_mode">
            <b-button
              variant="outline-success"
              size="sm"
              @click="onShowScan"
              :disabled="busyOrCannotModify"
            >
              <i class="fa fa-fw fa-barcode"></i>
              {{ trans('scan') }}
            </b-button>
          </b-col>
          <b-col v-if="scan_mode">
            <b-form-group>
              <b-form-input
                ref="scan"
                v-model="scanned_code"
                size="sm"
                :placeholder="trans('scan_equipment_barcode')"
                autocomplete="off"
                :disabled="busyOrCannotModify"
                @change="onCodeScanned"
              />
            </b-form-group>
          </b-col>
          <b-col v-if="scan_mode">
            <b-button
              variant="link"
              size="sm"
              class="text-danger ml-2"
              :disabled="busyOrCannotModify"
              @click="onHideScan"
            >
              <i class="fa fa-fw fa-times"></i>
            </b-button>
          </b-col>
        </b-row>
        <br/>

        <b-row>
          <b-col class="col-6">
            <b-form-group :label="trans('booking_type')">
              <booking-types-dropdown
                :organization="organization"
                v-model="booking_type_id"
                :disabled="busyOrCannotModify"
                :all-booking-types="all_booking_types"
                @all-booking-types="all_booking_types = $event"
              ></booking-types-dropdown>
            </b-form-group>
          </b-col>
          <b-col class="col-5 pr-0 text-right">
            <p class="mb-1">
              {{ events.length }}
              {{ trans('event' + (events.length > 1 || events.length == 0 && lang == 'en' ? 's' : '')).toLowerCase() }}
            </p>
            <p v-if="!!deltaEventsLength && !busy" class="mb-1 text-right">
              <small :class="deltaEventsLength > 0 ? 'text-success' : 'text-danger'">
                ({{ deltaEventsLength > 0 ? (`+${deltaEventsLength}`) : deltaEventsLength }}
                {{ trans('event' + (deltaEventsLength > 1 || deltaEventsLength == 0 && lang == 'en' ? 's' : '')).toLowerCase() }})
              </small>
            </p>
          </b-col>
          <b-col class="col-1 pl-0">
            &nbsp;
          </b-col>
        </b-row>

        <div v-if="!!booking && booking.invoice_item_id && !!invoiceItem" class="my-3">
          <p class="mb-1">
            <strong>{{ trans('bookings_ask_modify_invoice_item') }}</strong>
          </p>
          <b-form-radio-group
            v-model="invoice_item_change_mode"
            stacked
            :disabled="busyOrCannotModify"
          >
            <b-form-radio :value="null" :disabled="busyOrCannotModify">
                {{ trans('no') }}
            </b-form-radio>
            <b-form-radio :value="true" :disabled="busyOrCannotModify">
              {{ trans('yes') }}
              <div v-if="invoice_item_change_mode === true && invoiceItemCanBeModified" class="my-2">
                <b-row>
                  <b-col class="col-5 pr-0">
                    <b-form-group>
                      <label>
                        {{ trans('name') }}
                        <small class="text-muted" :title="trans('add_booking_name_about')" v-b-tooltip.hover>
                          <i class="fa fa-fw fa-info-circle"></i>
                        </small>
                      </label>
                      <b-form-input
                        v-model="invoice_item_name"
                        size="sm"
                        :state="!invoice_item_name ? false : null"
                        :disabled="busy"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col class="col-1 pr-0">
                    <b-form-group :label="trans('qty')">
                      <b-form-input
                        :value="invoiceItemCanBeModified ? events.length : originalInvoiceItemQuantity"
                        size="sm"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                  <b-col class="col-3 pr-0">
                    <b-form-group :label="trans('price')">
                      <b-form-input
                        v-if="!invoiceItemCanBeModified"
                        :value="originalInvoiceItemPrice"
                        size="sm"
                        readonly
                      />
                      <b-form-input
                        v-if="invoiceItemCanBeModified"
                        v-model="invoice_item_price"
                        type="number"
                        step="0.01"
                        min="0"
                        size="sm"
                        :state="invoiceItemPriceState"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col class="col-3">
                    <b-form-group :label="trans('amount')">
                      <b-form-input
                        :value="invoiceItemAmount"
                        size="sm"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
              <p v-if="invoice_item_change_mode === true && !invoiceItemCanBeModified && !busy" class="text-danger my-2">
                {{ trans('booking_manual_credit_to_adjust') }}
              </p>
            </b-form-radio>
            <b-form-radio value="only_name" :disabled="busyOrCannotModify">
              {{ trans('bookings_ask_modify_invoice_item_only_name') }}
              <b-form-group v-if="invoice_item_change_mode == 'only_name'" class="my-2">
                <label>
                  {{ trans('name') }}
                  <small class="text-muted" :title="trans('add_booking_name_about')" v-b-tooltip.hover>
                    <i class="fa fa-fw fa-info-circle"></i>
                  </small>
                </label>
                <b-form-input
                  v-model="invoice_item_name"
                  size="sm"
                  :state="!invoice_item_name ? false : null"
                  :disabled="busyOrCannotModify"
                />
              </b-form-group>
            </b-form-radio>
          </b-form-radio-group>
        </div>

        <div v-if="!!serializedForBuildInvoiceInfo" class="mt-3">
          <b-form-group>
            <b-form-checkbox switch v-model="new_booking_create_invoice" :disabled="busyOrCannotModify">
              {{ trans('create_invoice') }}?
            </b-form-checkbox>
          </b-form-group>

          <build-invoice-info-for-booking
            v-if="new_booking_create_invoice"
            :organization="organization"
            :booking="serializedForBuildInvoiceInfo"
            :pkg="pkg"
            v-model="new_booking_invoice_info"
            :all-taxes="allTaxes"
            @all-taxes="$emit('all-taxes', $event)"
            :all-gls="allGls"
            @all-gls="$emit('all-gls', $event)"
          ></build-invoice-info-for-booking>
        </div>
      </b-tab>

      <b-tab :title="trans('notes')" lazy>
        <b-row>
          <b-col class="col-7">
            <b-form-group :label="trans('notes_customer')">
              <html-textarea :organization="organization" v-model="notes" :disabled="busyOrCannotModify"></html-textarea>
              <b-form-text>{{ trans('notes_customer_about') }}</b-form-text>
            </b-form-group>
          </b-col>
          <b-col class="col-5">
            <b-form-group :label="trans('notes_private')">
              <b-form-textarea v-model="admin_notes" rows="6" :disabled="busyOrCannotModify"></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab v-if="!!booking" :title="trans('log')" lazy>
        <booking-log-table
          :organization="organization"
          :booking="booking"
          @restored="onRestored($event)"
        ></booking-log-table>
      </b-tab>

      <b-tab v-if="!!booking" :title="trans('clients')" lazy :disabled="!canModify">
        <b-row class="mb-2">
          <b-col class="pt-1">
            {{ !!linked_invoices ? linked_invoices.length : 0 }}
            {{ trans('client_invoices_linked_to_booking').toLowerCase() }}
          </b-col>
          <b-col class="col-auto">
            <b-button variant="primary" size="sm" v-b-modal.admin-create-client-invoice-linked-to-booking-modal :disabled="busyOrCannotModify">
              <i class="fa fa-fw fa-plus"></i>
              {{ trans('add') }}
            </b-button>
            <b-button variant="secondary" size="sm" class="ml-2" @click="onClientInvoicesRefresh" :disabled="busyOrCannotModify">
              <i class="fa fa-fw fa-redo" v-if="!busy"></i>
              <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
            </b-button>
          </b-col>
        </b-row>
        <b-table
          ref="client_invoices"
          class="bg-white"
          small
          fixed
          responsive
          show-empty
          :fields="linked_invoice_fields"
          :items="linkedInvoicesProvider"
        >
          <template #empty>
            <p align="center">{{ trans('none') }}</p>
          </template>
          <template #cell(invoice_no)="data">
            <b-link :href="`/organization/${organization.id}/invoice?invoice_no=${data.value}`" target="_blank">
              #{{ data.value }}
              <small>
                <i class="fa fa-fw fa-external-link-alt"></i>
              </small>
            </b-link>
          </template>
          <template #cell(recipient)="data">
            <div v-if="!!data.value">
              <div v-if="data.value.email != 'noclient@activitymessenger.com' && !!data.value.extra && !!data.value.extra.account_owner && !!data.value.extra.account_owner.company">{{ data.value.extra.account_owner.company }}</div>
              <div>{{ data.value.name }}</div>
              <small v-if="data.value.email != 'noclient@activitymessenger.com' && !!data.value.email" class="d-block">{{ data.value.email }}</small>
              <small v-if="data.value.email != 'noclient@activitymessenger.com' && !!data.value.mobile" class="d-block">{{ data.value.mobile }}</small>
            </div>
          </template>
          <template #cell(income)="data">
            {{ numeral(data.value).format('1,000.00') }}
          </template>
          <template #cell(balance)="data">
            {{ numeral(data.value).format('1,000.00') }}
          </template>
        </b-table>
      </b-tab>
    </b-tabs>
    <b-form-checkbox
      v-if="new_booking_create_invoice"
      v-model="sendClientEmailConfirmation"
    >
      {{trans('send_confirmation_email_to_client')}}
    </b-form-checkbox>
    <b-form-checkbox
      v-if="hasStaffLocations"
      v-model="sendPackageEmailConfirmation"
    >
      {{trans('send_confirmation_email_to_admin')}}
    </b-form-checkbox>
    <p v-if="hasConflicts" class="text-right text-danger mt-4">
      <i class="fa fa-fw fa-exclamation-triangle"></i>
      {{ conflictCount }} {{ trans('conflict' + (conflictCount > 1 || conflictCount == 0 && lang == en ? 's' : '')).toLowerCase() }}
    </p>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>

    <template #modal-footer>
      <b-row class="w-100">
        <b-col v-if="!hideDeleteDuplicate && !!booking && !!booking.id && ['cancelled', 'admin-deleted'].indexOf(booking.status) === -1 && canModify">
          <delete-button variant="outline-danger" @confirm="onDelete" :disabled="busyOrCannotModify">
            {{ trans('delete') }}
          </delete-button>
          <b-button variant="outline-primary" class="ml-2" @click="onClickDuplicate" :disabled="busyOrCannotModify || events.length == 0">
            {{ trans('duplicate') }}
          </b-button>
        </b-col>
        <b-col v-if="organization.has_bookings && !booking && canModify">
          <b-button variant="outline-secondary" @click="onClickClientBooking" :disabled="busyOrCannotModify">
            <i class="fa fa-fw fa-exchange-alt"></i>
            {{ trans('client_booking') }}
          </b-button>
        </b-col>
        <b-col class="text-right">
          <b-button variant="light" @click="$bvModal.hide(id)">
            {{ trans('cancel') }}
          </b-button>
          <b-button variant="primary" @click="onSave" :disabled="!canSave">
            {{ trans(!!booking && booking.id ? 'save' : 'create') }}
          </b-button>
        </b-col>
      </b-row>
    </template>

    <booking-add-weekly-recurrence-modal
      :organization="organization"
      :initial-start="lastStart"
      :initial-duration="lastDuration"
      :initial-location-ids="!!lastLocationId ? [lastLocationId] : null"
      :locations="locations"
      @add="onRecurrenceAdded"
    ></booking-add-weekly-recurrence-modal>

    <admin-create-client-invoice-linked-to-booking-modal
      :organization="organization"
      :booking="booking"
      @added="onClientInvoicesRefresh"
    ></admin-create-client-invoice-linked-to-booking-modal>
  </b-modal>
</template>

<script>
  import Treeselect from '@riophae/vue-treeselect'
  import Datetime from 'vue-bootstrap-datetimepicker'
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import DeleteButton from './../../../common/components/DeleteConfirmButton'
  import BookingStatusBadge from './BookingStatusBadge'
  import BookingLogTable from './BookingLogTable'
  import BookingEventsCache from './BookingEventsCache'
  import BookLocation from './BookLocation'
  import BookingTypesDropdown from './../booking-type/BookingTypesDropdown'
  import BookingAddWeeklyRecurrenceModal from './BookingAddWeeklyRecurrenceModal'
  import AdminCreateClientInvoiceLinkedToBookingModal from './AdminCreateClientInvoiceLinkedToBookingModal'
  import BuildInvoiceInfoForBooking from './BuildInvoiceInfoForBooking'
  import StaffAvailabilityEventsCache from './../calendar/StaffAvailabilityEventsCache'

  let dateFormat = 'ddd LL'
  let timeFormat = 'LT'

  function getEventStartAsMoment(event) {
    let start = moment(event.start_date, dateFormat)
    let time = moment(event.start_time, timeFormat)
    start.set({
      hour: time.get('hour'),
      minute: time.get('minute'),
    })
    return start
  }

  let blank = {
    id: null,
    location_id: null,
    start_date: null,
    start_time: null,
    duration: 60,
  }

  function cloneEvent(event) {
    let clone = _.clone(event)
    clone.id = null
    return clone
  }

  function cloneBooking(booking) {
    return {
      id: null,
      name: booking.name,
      package_id: null,
      notes: booking.notes,
      admin_notes: booking.admin_notes,
      start_date: booking.start_date,
      start_time: booking.start_time,
      events: _.map(booking.events, event => {
        return cloneEvent(event)
      })
    }
  }

  export default {
    props: {
      id: {
        type: String,
        default: 'booking-modal',
      },
      organization: Object,
      allBookingTypes: Array,
      booking: Object,
      invoiceItem: Object, // Can be null
      start: String,
      end: String,
      locationId: {
        type: Number,
        default: null,
      },
      availability: Object,
      hideDeleteDuplicate: {
        type: Boolean,
        default: false,
      },
      allTaxes: Array,
      allGls: Array,
    },
    components: {
      Treeselect,
      Datetime,
      HtmlTextarea,
      DeleteButton,
      BookingStatusBadge,
      BookingLogTable,
      BookingEventsCache,
      StaffAvailabilityEventsCache,
      BookLocation,
      BookingTypesDropdown,
      BookingAddWeeklyRecurrenceModal,
      AdminCreateClientInvoiceLinkedToBookingModal,
      BuildInvoiceInfoForBooking,
    },
    data() {
      return {
        trans: trans,
        lang: lang,
        numeral: numeral,
        moment: moment,
        busy: false,
        error: null,
        visible: false,
        tab: 0,
        show_apply_package_rules: false,
        name: null,
        invoice_item_change_mode: null,
        invoice_item_name: null,
        invoice_item_price: null,
        new_booking_create_invoice: false,
        new_booking_invoice_info: null,
        package_id: null,
        packages: null,
        notes: null,
        admin_notes: null,
        start_date: moment().format(dateFormat),
        start_time: moment().format(timeFormat),
        locations: null,
        events: [cloneEvent(blank)],
        dateOptions: {
          locale: window.lang,
          format: dateFormat,
        },
        timeOptions: {
          locale: window.lang,
          format: timeFormat,
          stepping: 5,
        },
        booking_type_id: null,
        all_booking_types: this.allBookingTypes,
        mode: 'duration',
        modeOptions: [
          {value: 'duration', text: trans('duration')},
          {value: 'end', text: trans('end')},
        ],
        scan_mode: false,
        scanned_code: null,
        scanned_location_ids: [],
        fetch_events_fn: null,
        fetch_availabilities_fn: null,
        //
        linked_invoices: null,
        linked_invoice_fields: [
          {key: 'invoice_no', label: 'No'},
          {key: 'recipient', label: trans('client')},
          {key: 'income', label: trans('total') + ' $'},
          {key: 'balance', label: trans('balance') + ' $'},
        ],
        show_passed_events: false,
        sendClientEmailConfirmation: true,
        sendPackageEmailConfirmation: true,
      }
    },
    computed: {
      hasStaffLocations() {
        if(this.events && this.locations){
          const staffLocationIds = this.locations
            .filter(location => location.type === "staff")
            .map(location => location.id);
  
          return this.events.some(event => staffLocationIds.includes(event.location_id));
        }
        return false
      },
      passedCount() {
        let now = moment().startOf('day')
        return _.reduce(this.events, (count, e) => {
          if (!!e.start_date && moment(e.start_date, dateFormat).isBefore(now)) count +=1
          return count
        }, 0)
      },
      visibleIndices() {
        let now = moment().startOf('day')
        return _.reduce(this.events, (indices, e, index) => {
          if (this.show_passed_events ||
            !e.start_date ||
            moment(e.start_date, dateFormat).isSameOrAfter(now)) {
            indices.push(index)
          }
          return indices
        }, [])
      },
      nameState() {
        return !!this.name && !!this.name.trim()
      },
      packageOptions() {
        let options = [{
          value: null,
          text: trans('custom'),
        }]
        if (this.packages) {
          for (var i = 0; i < this.packages.length; i++)
            options.push({
              value: this.packages[i].id,
              text: this.packages[i].name,
            })
        }
        return options
      },
      canModify() {
        return ['owner', 'admin', 'attendant'].indexOf(this.organization.auth_user.permission) >= 0 ||
          this.organization.auth_user.can_own_bookings && (!this.booking || this.booking.user_id == this.organization.auth_user.id)
      },
      pkg() {
        if (!this.package_id || !this.packages) return null
        return _.find(this.packages, {id: this.package_id})
      },
      hasEquipmentWithSkus() {
        return _.find(this.locations || [], location => {
          if (location.type == 'equipment' && !!location.sku) return true
          return false
        })
      },
      conflictCount() {
        return _.reduce(this.events || [], (count, event) => {
          if (event.conflict) count += 1
          return count
        }, 0)
      },
      hasConflicts() {
        return this.conflictCount > 0
      },
      lastStart() {
        if (!this.events || this.events.length == 0) return null
        let event = this.events[this.events.length-1]
        let start = getEventStartAsMoment(event)
        return start.toISOString()
      },
      lastDuration() {
        return this.events && this.events.length > 0 ?
          this.events[this.events.length-1].duration :
          60
      },
      lastLocationId() {
        return this.events && this.events.length > 0 ?
          this.events[this.events.length-1].location_id :
          null
      },
      invoiceItemCanBeModified() {
        return !!this.invoiceItem && !this.invoiceItem.was_paid
      },
      originalBookingEventsLength() {
        return !!this.booking ? this.booking.events.length : null
      },
      deltaEventsLength() {
        if (this.originalBookingEventsLength === null) return null
        return this.events.length - this.originalBookingEventsLength
      },
      originalInvoiceItemQuantity() {
        return !!this.invoiceItem ? this.invoiceItem.quantity : null
      },
      originalInvoiceItemPrice() {
        if (!this.invoiceItem) return null
        return this.invoiceItem.price
      },
      invoiceItemPriceNumber() {
        if (this.invoice_item_price === null || isNaN(this.invoice_item_price)) return null
        return _.round(this.invoice_item_price, 2)
      },
      invoiceItemPriceState() {
        if (this.invoiceItemPriceNumber === null) return false
        return null
      },
      invoiceItemAmount() {
        if (this.invoiceItemPriceState === false) return null
        return _.round(this.invoiceItemPriceNumber * this.events.length, 2)
      },
      minStartFromEvents() {
        return _.reduce(this.events, (min, event) => {
          let start = getEventStartAsMoment(event)
          if (this.pkg) {
            let rule = _.reduce(this.pkg.rules, (found, rule) => {
              if (rule.location_ids.indexOf(event.location_id) >= 0) found = rule
              return found
            }, null)
            if (rule && rule.do_not_reserve) {
              // Skip...
              if (min) start = min
            }
            else if (rule && rule.pre_buffer) start.add(rule.pre_buffer, 'minute')
          }
          if (!min || start.isBefore(min)) min = start
          return min
        }, null)
      },
      serializedEvents() {
        return _.map(this.events, (r) => {
          let start = getEventStartAsMoment(r)
          let end = moment(start).add(r.duration, 'minute')
          return {
            id: r.id,
            location_id: r.location_id,
            start: start.toISOString(),
            end: end.toISOString(),
            am_class_id: r.am_class_id,
            booking_id: r.booking_id,
            duration: r.duration,
          }
        })
      },
      serialized() {
        return {
          name: this.name,
          invoice_item_name: this.invoice_item_change_mode !== null ? this.invoice_item_name : null,
          invoice_item_quantity: this.invoice_item_change_mode === true && this.invoiceItemCanBeModified ? this.serializedEvents.length : null,
          invoice_item_price: this.invoice_item_change_mode === true && this.invoiceItemCanBeModified ? this.invoiceItemPriceNumber : null,
          new_booking_invoice_info: this.new_booking_create_invoice ? this.new_booking_invoice_info : null,
          package_id: this.package_id,
          am_class_id: !!this.booking ? this.booking.am_class_id : null,
          event_id: !!this.booking ? this.booking.event_id : null,
          events: this.serializedEvents,
          notes: this.notes,
          start: this.minStartFromEvents ? this.minStartFromEvents.toISOString() : null,
          admin_notes: this.admin_notes,
          booking_type_id: this.booking_type_id,
          send_client_email_confirmation: this.sendClientEmailConfirmation ?? false,
          send_package_email_confirmation: this.sendPackageEmailConfirmation ?? false,
        }
      },
      serializedForBuildInvoiceInfo() {
        if (!!this.booking || !this.name || this.events.length == 0) return null
        let serialized = _.pick(this.serialized, ['name', 'start', 'package_id', 'events'])
        return serialized
      },
      busyOrCannotModify() {
        return this.busy || !this.canModify
      },
      canSave() {
        return !this.busyOrCannotModify && !!this.name && this.events.length > 0
      },
    },
    watch: {
      allBookingTypes() {
        this.all_booking_types = this.allBookingTypes
      },
    },
    methods: {
      canShow(e, index) {
        return this.visibleIndices.indexOf(index) >= 0
      },
      isFirstShown(e, index) {
        return this.visibleIndices.indexOf(index) === 0
      },
      onClickSort() {
        this.events = _.sortBy(this.events, function(event) {
          return getEventStartAsMoment(event).toISOString()
        })
      },
      onLocationChanged(location) {
        if (!location) return
        let index = _.findIndex(this.locations || [], {id: location.id})
        if (index !== -1) {
          this.$set(this.locations, index, location)
          this.$emit('saved')
        }
      },
      onClientInvoicesRefresh() {
        this.$refs.client_invoices.refresh()
      },
      linkedInvoicesProvider() {
        this.linked_invoices = null
        if (!this.booking || !this.booking.id) return
        this.busy = true
        this.error = null
        const url = `/organization/${this.organization.id}/invoice/booking/${this.booking.id}/all`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.linked_invoices = response.data
          return this.linked_invoices
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          this.linked_invoices = []
          return this.linked_invoices
        })
      },
      onShowScan() {
        this.scan_mode = true
        setTimeout(() => {
          this.$refs.scan.focus()
        }, 250)
      },
      onHideScan() {
        this.scan_mode = false
        this.scanned_code = null
        this.scanned_location_ids = []
      },
      onCodeScanned() {
        if (!this.scanned_code) return
        let location = _.find(this.locations || [], {sku: this.scanned_code})
        if (!!location) {
          this.$nextTick(() => {
            this.scanned_code = null
            if (!_.find(this.events, {location_id: location.id})) {
              this.onAddRow(false, location.id)
            }
            if (this.scanned_location_ids.indexOf(event.location_id) === -1) {
              this.scanned_location_ids.push(location.id)
            }
          })
        }
      },
      getVariant(event) {
        if (!this.scan_mode || !event.location_id) return null
        return this.scanned_location_ids.indexOf(event.location_id) >= 0 ? 'success': 'info'
      },
      onAddRow(sequence, location_id) {
        let clone = cloneEvent(this.events.length > 0 ? this.events[this.events.length-1] : blank)
        if (sequence === true) {
          clone = _.identifySequenceAndAdjustDateOfEvent(clone, this.events, dateFormat, timeFormat)
        } else {
          clone.location_id = null
        }
        if (!!location_id) clone.location_id = location_id
        this.events.push(clone)
      },
      onDeleteRow(index) {
        this.events.splice(index, 1)
      },
      onClickAddRecurrence() {
        this.$bvModal.show('booking-add-weekly-recurrence-modal')
      },
      onRecurrenceAdded(dates, location_ids, duration) {
        _.each(dates, date => {
          let event = _.assign({}, blank, {
            start_date: moment(date).format(dateFormat),
            start_time: moment(date).format(timeFormat),
            duration: duration,
          })
          _.each(location_ids, location_id => {
            this.events.push(_.assign({}, event, {location_id: location_id}))
          })
        })
      },
      onClickClientBooking() {
        this.$emit('click-client-booking')
        this.$bvModal.hide(this.id)
      },
      onClickDuplicate() {
        this.$bvModal.hide(this.id)
        this.$emit('duplicate', cloneBooking(this.booking))
      },
      onApplyPackage() {
        if (!this.package_id) return
        let pkg = _.find(this.packages, {id: this.package_id})
        if (!this.name) this.name = pkg.name
        this.events = _.map(pkg.rules, rule => {
          return {
            id: null,
            location_id: rule.location_ids[0],
            start_date: this.start_date,
            start_time: moment(this.start_time, timeFormat).add(-rule.pre_buffer, 'minute').format(timeFormat),
            duration: rule.duration + rule.pre_buffer + rule.post_buffer,
          }
        })
        this.show_apply_package_rules = false
      },
      onShown() {
        if (!!this.start && !!this.end &&
          this.mode == 'duration' &&
          moment.duration(moment(this.end).diff(this.start)).asHours() > 18) {
          this.mode = 'end'
        }
        this.loadLocations().then(() => {
          this.loadPackages().then(() => {
            this.init()
          })
        })
      },
      init() {
        this.tab = 0
        this.show_passed_events = !this.booking
        if (this.booking) {
          this.name = this.booking.name
          this.package_id = this.booking.package_id
          this.notes = this.booking.notes
          this.admin_notes = this.booking.admin_notes
          this.start_date = moment(this.booking.start).format(dateFormat)
          this.start_time = moment(this.booking.start).format(timeFormat)
          let max_duration = 0
          let events = _.reduce(this.booking.events, (events, event) => {
            if (!!event.deleted_at) return events
            let start = moment(event.start)
            let end = moment(event.end)
            let duration = end.diff(start, 'minute')
            if (duration > max_duration) max_duration = duration
            events.push({
              id: event.id,
              location_id: event.location_id,
              start_date: start.format(dateFormat),
              start_time: start.format(timeFormat),
              duration: duration,
              // for classes
              am_class_id: event.am_class_id,
              booking_id: event.booking_id,
            })
            return events
          }, [])
          this.events = _.sortBy(events, function(event) {
            return getEventStartAsMoment(event).toISOString()
          })
          this.mode = max_duration <= 18*60 ? 'duration' : 'end'
          this.booking_type_id = this.booking.booking_type_id
          this.invoice_item_name = !!this.invoiceItem ? this.invoiceItem.name.split('@')[0].trim() : null
          this.invoice_item_price = !!this.invoiceItem ? this.invoiceItem.price : null
        }
        else if (this.availability) {
          let start = moment(this.availability.start)
          let end = moment(this.availability.end)
          this.start_date = moment(start).format(dateFormat)
          this.start_time = moment(start).format(timeFormat)
          event.duration = end.diff(start, 'minute')
          this.name = this.availability.package_name
          this.package_id = this.availability.package_id
          this.onApplyPackage()
          for (let i = 0; i < this.events.length; i++)
            this.events[i].location_id = this.availability.availability[i].location_ids[0]
        }
        else if (this.start && this.end) {
          let event = cloneEvent(blank)
          let start = moment(this.start)
          let end = moment(this.end)
          this.start_date = moment(start).format(dateFormat)
          this.start_time = moment(start).format(timeFormat)
          event.start_date = start.format(dateFormat)
          event.start_time = start.format(timeFormat)
          event.duration = end.diff(start, 'minute')
          event.location_id = this.locationId || (!!this.events && !!this.events[0] && !!this.events[0].location_id ? this.events[0].location_id : null)
          this.events = [event]
        }
        this.visible = true
      },
      onHidden() {
        this.busy = false
        this.error = false
        this.visible = false
        this.locations = null
        this.packages = null
        this.new_booking_create_invoice = false
        this.new_booking_invoice_info = null
        if (this.booking) this.reset()
        this.cache = {}
        this.fetch_events_fn = null
        this.fetch_availabilities_fn = null
        this.scan_mode = false
        this.scanned_code = null
        this.scanned_location_ids = []
        this.tab = 0
        this.$emit('hidden')
      },
      reset() {
        this.name = null
        this.package_id = null
        this.notes = null
        this.admin_notes = null
        this.start_date = moment().format(dateFormat)
        this.start_time = moment().format(timeFormat)
        this.show_apply_package_rules = false
        this.invoice_item_change_mode = null
        this.invoice_item_name = null
        this.invoice_item_price = null
        this.events = [cloneEvent(blank)]
        this.booking_type_id = null
      },
      validate() {
        for (let i = 0; i < this.events.length; i++) {
          let event = this.events[i]
          if (!event.location_id)
            return trans('reservation_missing_field').replace(':field', trans('location'))
          if (!event.start_date)
            return trans('reservation_missing_field').replace(':field', trans('date'))
          if (!event.start_time)
            return trans('reservation_missing_field').replace(':field', trans('time'))
        }
        return true
      },
      onSave(e) {
        e.preventDefault()
        if (!this.hasConflicts) return this.save()
        this.$bvModal.msgBoxConfirm(trans('confirm_booking_has_conflicts'), {
          title: trans('booking'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: trans('yes'),
          cancelTitle: trans('cancel'),
        })
        .then(value => {
          if (value) this.save()
        })
      },
      onRestored(booking) {
        this.$emit('saved', booking)
        this.$bvModal.hide(this.id)
        this.reset()
      },
      save() {
        this.busy = true
        this.error = null

        let validation = this.validate()
        if (validation !== true) {
          this.error = validation
          this.busy = false
          return
        }

        let url = `/organization/${this.organization.id}/booking`
        let method = 'post'
        if (this.booking && this.booking.id) {
          url += `/${this.booking.id}`
          method = 'patch'
        }
        axios[method](url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide(this.id)
          this.reset()
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onDelete() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/booking/${this.booking.id}`
        axios.delete(url)
        .then(response => {
          this.busy = false
          this.$emit('deleted')
          this.$bvModal.hide(this.id)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      loadLocations() {
        if (this.locations) return
        this.busy = true
        this.error = null
        const url = `/organization/${this.organization.id}/location/list?per_page=500`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.locations = response.data.data
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      loadPackages() {
        if (this.packages) return
        this.busy = true
        this.error = null
        const url = `/organization/${this.organization.id}/package/list?per_page=100`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.packages = response.data.data
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window[_.camelCase(this.id)] = this
    },
  }
</script>